/**
 * CommentsPanel — notes on a project/opportunity (shared: the Projects tab and
 * the Opportunities tab are the same rows keyed by id). Self-contained: own
 * state, fetches on mount, POSTs then refetches.
 *
 * API: GET/POST /api/projects/:projectId/comments,
 *      DELETE   /api/projects/:projectId/comments/:commentId
 *
 * Auth is automatic — window.fetch is patched (src/index.js) to attach the
 * Entra bearer token to every /api call.
 */
import { useState, useEffect, useCallback } from 'react';
import { notifyCommentsChanged } from '../../hooks/useCommentsIndex';
import { severityTone } from '../../data/projectHealth';
import { useApiBase } from '../../context/DataScope';
import { useLiveRefresh, matchRecord } from '../../live/useLive';

const GREEN = '#1B5E20';

function formatWhen(iso) {
  if (!iso) return '';
  const d = new Date(iso);
  if (isNaN(d)) return String(iso);
  return d.toLocaleString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });
}

export default function CommentsPanel({ projectId, onCount }) {
  const [comments, setComments] = useState([]);
  const [draft, setDraft] = useState('');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const base = useApiBase(); // the KSA tab swaps in its own prefix (context/DataScope.js)

  const load = useCallback(async () => {
    if (!projectId) return;
    try {
      const r = await fetch(`${base}/projects/${encodeURIComponent(projectId)}/comments`);
      if (r.ok) {
        const list = await r.json();
        setComments(list);
        if (onCount) onCount(list.length); // keep the Doc Hub tab badge live
      }
    } catch {
      /* offline/dev — keep whatever is shown */
    } finally {
      setLoading(false);
    }
  }, [projectId, onCount, base]);

  useEffect(() => {
    setLoading(true);
    load();
  }, [load]);
  // someone else's note on this record shows up here without a refresh (src/live)
  useLiveRefresh(null, load, { match: matchRecord(base, projectId, ['comments']) });

  const add = useCallback(async () => {
    const body = draft.trim();
    if (!body || saving) return;
    setSaving(true);
    try {
      const r = await fetch(`${base}/projects/${encodeURIComponent(projectId)}/comments`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ body }),
      });
      if (r.ok) {
        setDraft('');
        await load();
        notifyCommentsChanged();
      }
    } catch {
      /* noop — leave the draft so the user can retry */
    } finally {
      setSaving(false);
    }
  }, [draft, saving, projectId, load, base]);

  // Editing keeps the author and the timestamp, which delete-and-retype lost.
  const [editing, setEditing] = useState(null); // { id, body }
  const saveEdit = useCallback(async () => {
    if (!editing || !editing.body.trim()) return;
    try {
      const r = await fetch(`${base}/projects/${encodeURIComponent(projectId)}/comments/${encodeURIComponent(editing.id)}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ body: editing.body.trim() }),
      });
      if (r.ok) {
        setEditing(null);
        await load();
        notifyCommentsChanged();
      }
    } catch {
      /* leave the editor open so the text is not lost */
    }
  }, [editing, projectId, load, base]);

  const remove = useCallback(
    async (commentId) => {
      try {
        const r = await fetch(`${base}/projects/${encodeURIComponent(projectId)}/comments/${encodeURIComponent(commentId)}`, {
          method: 'DELETE',
        });
        if (r.ok) {
          await load();
          notifyCommentsChanged();
        }
      } catch {
        /* noop */
      }
    },
    [projectId, load, base]
  );

  return (
    <div style={{ fontFamily: "'Montserrat', sans-serif" }}>
      <div style={{ fontSize: 10, fontWeight: 800, color: GREEN, letterSpacing: 1.6, textTransform: 'uppercase', marginBottom: 10 }}>
        Comments
      </div>

      {/* Composer */}
      <div style={{ marginBottom: 14 }}>
        <textarea
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          placeholder="Add a note…"
          style={{
            width: '100%',
            minHeight: 56,
            resize: 'vertical',
            boxSizing: 'border-box',
            padding: '8px 10px',
            borderRadius: 8,
            border: '1px solid #D7E3D3',
            fontSize: 11.5,
            fontFamily: 'Montserrat',
            outline: 'none',
            color: '#1a1a1a',
            background: '#fff',
          }}
        />
        <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 6 }}>
          <button
            type="button"
            onClick={add}
            disabled={!draft.trim() || saving}
            style={{
              background: !draft.trim() || saving ? '#A5C4A7' : GREEN,
              color: '#fff',
              border: 'none',
              padding: '7px 16px',
              borderRadius: 6,
              fontWeight: 700,
              fontSize: 11,
              cursor: !draft.trim() || saving ? 'default' : 'pointer',
              fontFamily: 'Montserrat',
            }}
          >
            {saving ? 'Adding…' : 'Add'}
          </button>
        </div>
      </div>

      {/* Notes stack */}
      {loading ? (
        <div style={{ fontSize: 11, color: '#aaa', padding: '12px 2px' }}>Loading…</div>
      ) : comments.length === 0 ? (
        <div style={{ fontSize: 11, color: '#bbb', padding: '12px 2px' }}>No comments yet</div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
          {comments.map((c) => {
            // A comment written against a yellow/red flag wears that colour,
            // so the reason for the flag is visible at a glance.
            const tone = severityTone(c.severity);
            return (
              <div
                key={c.id}
                style={{
                  position: 'relative',
                  background: tone ? tone.bg : '#F1F8E9',
                  border: `1px solid ${tone ? tone.dot : '#DCEDC8'}`,
                  borderLeft: `3px solid ${tone ? tone.dot : GREEN}`,
                  borderRadius: 8,
                  padding: '10px 12px',
                  boxShadow: '0 1px 2px rgba(27,94,32,0.06)',
                }}
              >
                {tone && (
                  <div
                    style={{
                      fontSize: 8.5,
                      fontWeight: 800,
                      letterSpacing: 1,
                      textTransform: 'uppercase',
                      color: tone.fg,
                      marginBottom: 4,
                    }}
                  >
                    {tone.label} flag
                  </div>
                )}
                {editing && editing.id === c.id ? (
                  <>
                    <textarea
                      autoFocus
                      value={editing.body}
                      onChange={(e) => setEditing((ed) => ({ ...ed, body: e.target.value }))}
                      onKeyDown={(e) => {
                        if (e.key === 'Escape') setEditing(null);
                        if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) saveEdit();
                      }}
                      style={{
                        width: '100%',
                        minHeight: 56,
                        resize: 'vertical',
                        boxSizing: 'border-box',
                        padding: '8px 10px',
                        borderRadius: 6,
                        border: '1px solid #C9D6C4',
                        fontSize: 12,
                        fontFamily: 'Montserrat',
                        outline: 'none',
                        color: '#1a1a1a',
                        background: '#fff',
                      }}
                    />
                    <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 12, marginTop: 7 }}>
                      <button
                        type="button"
                        onClick={() => setEditing(null)}
                        style={{
                          background: 'none',
                          border: 'none',
                          padding: 0,
                          cursor: 'pointer',
                          fontSize: 10.5,
                          fontWeight: 700,
                          color: '#B71C1C',
                          fontFamily: 'Montserrat',
                        }}
                      >
                        Cancel
                      </button>
                      <button
                        type="button"
                        onClick={saveEdit}
                        disabled={!editing.body.trim()}
                        style={{
                          background: 'none',
                          border: 'none',
                          padding: 0,
                          cursor: 'pointer',
                          fontSize: 10.5,
                          fontWeight: 700,
                          color: GREEN,
                          fontFamily: 'Montserrat',
                        }}
                      >
                        Save
                      </button>
                    </div>
                  </>
                ) : (
                  <>
                    <div
                      style={{
                        fontSize: 12,
                        color: tone ? tone.fg : '#243024',
                        lineHeight: 1.5,
                        whiteSpace: 'pre-wrap',
                        wordBreak: 'break-word',
                      }}
                    >
                      {c.body}
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginTop: 6 }}>
                      <span style={{ fontSize: 9.5, fontWeight: 700, color: '#6b7a67', letterSpacing: 0.2 }}>
                        {c.author || 'Unknown'} <span style={{ color: '#b7c2b3' }}>·</span> {formatWhen(c.created_at)}
                      </span>
                      <span style={{ marginLeft: 'auto', display: 'flex', gap: 10 }}>
                        <button
                          type="button"
                          aria-label="Edit comment"
                          title="Edit this comment"
                          onClick={() => setEditing({ id: c.id, body: c.body })}
                          style={{
                            background: 'none',
                            border: 'none',
                            padding: 0,
                            cursor: 'pointer',
                            fontSize: 10,
                            fontWeight: 700,
                            color: GREEN,
                            fontFamily: 'Montserrat',
                          }}
                        >
                          Edit
                        </button>
                        <button
                          type="button"
                          aria-label="Delete comment"
                          title="Delete this comment"
                          onClick={() => remove(c.id)}
                          style={{
                            background: 'none',
                            border: 'none',
                            padding: 0,
                            cursor: 'pointer',
                            fontSize: 10,
                            fontWeight: 700,
                            color: '#B4837F',
                            fontFamily: 'Montserrat',
                          }}
                        >
                          Delete
                        </button>
                      </span>
                    </div>
                  </>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
